import React, { useMemo, useState } from 'react'
import { collection, documentId, limit, onSnapshot, orderBy, query } from 'firebase/firestore'
import { httpsCallable } from 'firebase/functions'
import { db, functions } from '../../firebase-config'

function pct(value) {
  return `${(Number(value || 0) * 100).toFixed(2)}%`
}

export default function AnalyticsDashboard() {
  const [rows, setRows] = useState([])
  const [loading, setLoading] = useState(true)
  const [syncing, setSyncing] = useState(false)
  const [msg, setMsg] = useState('')

  React.useEffect(() => {
    const q = query(
      collection(db, 'analytics_daily'),
      orderBy(documentId(), 'desc'),
      limit(30)
    )
    const unsub = onSnapshot(q, (snap) => {
      setRows(snap.docs.map((d) => ({ id: d.id, ...d.data() })))
      setLoading(false)
      setMsg('')
    }, (e) => {
      setLoading(false)
      setMsg(e?.message || 'Sin permisos para leer analytics_daily. Refresca sesion admin.')
    })
    return () => unsub()
  }, [])

  const latest = rows[0] || null

  const sentiment = useMemo(() => {
    const agg = { duda_legal: 0, error_sistema: 0, sin_clasificar: 0 }
    rows.forEach((r) => {
      const s = r.sentiment_split || {}
      agg.duda_legal += Number(s.duda_legal || 0)
      agg.error_sistema += Number(s.error_sistema || 0)
      agg.sin_clasificar += Number(s.sin_clasificar || 0)
    })
    const total = agg.duda_legal + agg.error_sistema + agg.sin_clasificar
    return { agg, total }
  }, [rows])

  const normativeTop = useMemo(() => {
    const acc = {}
    rows.forEach((r) => {
      const usage = r.normative_usage || {}
      Object.keys(usage).forEach((k) => {
        acc[k] = (acc[k] || 0) + Number(usage[k] || 0)
      })
    })
    return Object.entries(acc)
      .sort((a, b) => b[1] - a[1])
      .slice(0, 6)
  }, [rows])

  const syncNow = async () => {
    setSyncing(true)
    setMsg('Sincronizando analytics...')
    try {
      const callSync = httpsCallable(functions, 'triggerManualAnalyticsSync')
      const resp = await callSync({})
      setMsg(`Sincronizacion OK (${resp?.data?.key || 'hoy'})`)
    } catch (e) {
      setMsg(e?.message || 'No se pudo sincronizar analytics.')
    } finally {
      setSyncing(false)
    }
  }

  return (
    <section className="rounded-xl border border-slate-800 bg-slate-900 p-4 shadow-sm">
      <div className="flex items-center justify-between gap-2">
        <h3 className="text-lg font-bold text-cyan-100">KPIs & Observabilidad</h3>
        <button
          type="button"
          onClick={syncNow}
          disabled={syncing}
          className="rounded bg-cyan-700 px-3 py-1 text-xs font-semibold text-white hover:bg-cyan-600 disabled:bg-slate-600"
        >
          {syncing ? 'Sincronizando...' : 'Sincronizar ahora'}
        </button>
      </div>
      {msg && <p className="mt-2 text-xs text-slate-300">{msg}</p>}

      {loading && <p className="mt-3 text-sm text-slate-400">Cargando analytics...</p>}
      {!loading && !latest && <p className="mt-3 text-sm text-slate-400">Sin datos agregados aun.</p>}

      {!loading && latest && (
        <>
          <div className="mt-4 grid gap-3 md:grid-cols-3">
            <div className="rounded-lg border border-slate-700 bg-slate-950 p-3">
              <p className="text-xs uppercase text-slate-400">Tasa de Incidencia</p>
              <p className={`text-2xl font-bold ${Number(latest.tasa_incidencia || 0) > 0.05 ? 'text-rose-700' : 'text-emerald-700'}`}>
                {pct(latest.tasa_incidencia)}
              </p>
            </div>
            <div className="rounded-lg border border-slate-700 bg-slate-950 p-3">
              <p className="text-xs uppercase text-slate-400">SLA Promedio</p>
              <p className="text-2xl font-bold text-slate-100">{Number(latest.sla_avg_minutes || 0)} min</p>
            </div>
            <div className="rounded-lg border border-slate-700 bg-slate-950 p-3">
              <p className="text-xs uppercase text-slate-400">Tickets de Hoy</p>
              <p className="text-2xl font-bold text-slate-100">{Number(latest.total_tickets || 0)}</p>
            </div>
          </div>

          <div className="mt-4 grid gap-3 md:grid-cols-2">
            <div className="rounded-lg border border-slate-700 bg-slate-950 p-3">
              <h4 className="text-sm font-semibold text-cyan-100">Monitor de Sentiment (30 dias)</h4>
              <div className="mt-2 space-y-2 text-xs">
                <p>Error de Sistema: <strong>{sentiment.agg.error_sistema}</strong></p>
                <p>Duda Legal: <strong>{sentiment.agg.duda_legal}</strong></p>
                <p>Sin clasificar: <strong>{sentiment.agg.sin_clasificar}</strong></p>
                <p className="text-slate-400">Total eventos: {sentiment.total}</p>
              </div>
            </div>
            <div className="rounded-lg border border-slate-700 bg-slate-950 p-3">
              <h4 className="text-sm font-semibold text-cyan-100">Mapa Normativo (Top)</h4>
              <div className="mt-2 space-y-1 text-xs">
                {normativeTop.length === 0 && <p className="text-slate-400">Sin uso registrado.</p>}
                {normativeTop.map(([key, count]) => (
                  <div key={key} className="flex items-center justify-between gap-2">
                    <span className="truncate text-slate-200">{key}</span>
                    <span className="rounded bg-slate-800 px-2 py-0.5 font-semibold text-slate-200">{count}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </>
      )}
    </section>
  )
}
